// src/pages/admin/RiderTracking.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import SearchIcon from "@mui/icons-material/Search";
import RoomIcon from "@mui/icons-material/Room";
import TimelineIcon from "@mui/icons-material/Timeline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RadioButtonCheckedIcon from "@mui/icons-material/RadioButtonChecked";

import { io } from "socket.io-client";
import api from "../../api/api";
import { useSnackbar } from "../../utils/snackbar";
import { useLocation } from "react-router-dom";

/* ======================================================================
   ADVANCED RIDER TRACKING (ADMIN)
   ====================================================================== */

export default function RiderTracking() {
  const [riders, setRiders] = useState([]);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [selectedRiderId, setSelectedRiderId] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);

  // liveLocations: { riderId: { lat, lng, ts } }
  const [liveLocations, setLiveLocations] = useState({});
  // orderLocations: { orderId: { lat, lng, ts } }
  const [orderLocations, setOrderLocations] = useState({});

  const socketRef = useRef(null);
  const { showAlert } = useSnackbar();
  const locationHook = useLocation();

  /* ------------------------------------------------------
     LOAD INITIAL RIDERS + ORDERS
  ------------------------------------------------------ */
  const load = async () => {
    try {
      const res = await api.get("/api/admin/riders/with-orders");
      setRiders(res.data.riders || []);
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error(err);
      showAlert("Failed to load rider data", "error");
    }
  };

  useEffect(() => {
    load();
  }, []);

  /* ------------------------------------------------------
     INITIAL FILTER FROM URL (?rider=...&order=...)
  ------------------------------------------------------ */
  useEffect(() => {
    const params = new URLSearchParams(locationHook.search);
    const riderParam = params.get("rider");
    const orderParam = params.get("order");

    if (riderParam) setSelectedRiderId(riderParam);
    if (orderParam) setSelectedOrderId(orderParam);
  }, [locationHook.search]);

  /* ------------------------------------------------------
     HELPER: UPSERT RIDER
  ------------------------------------------------------ */
  const upsertRider = (incoming) => {
    if (!incoming || !incoming._id) return;

    setRiders((prev) => {
      const idx = prev.findIndex((r) => r._id === incoming._id);

      const updatedRider = {
        ...prev[idx],
        ...incoming,
        _updatedAt: Date.now(), // 🔥 FORCE REACT UPDATE
      };

      // Rider does NOT exist → add new
      if (idx === -1) return [...prev, updatedRider];

      // Rider exists → replace
      const copy = [...prev];
      copy[idx] = updatedRider;
      return copy;
    });
  };

  /* ------------------------------------------------------
     SOCKET.IO SETUP (REALTIME TRACKING)
  ------------------------------------------------------ */
  useEffect(() => {
    const baseURL =
      (api.defaults && api.defaults.baseURL) || window.location.origin;

    const socket = io(baseURL.replace(/\/+$/, ""), {
      transports: ["websocket"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("🔌 RiderTracking connected:", socket.id);
    });

    // Rider online
    socket.on("rider:online", (payload = {}) => {
      const { riderId, rider, lastSeen } = payload;
      if (!riderId) return;

      upsertRider({
        ...(rider || {}),
        _id: riderId,
        isOnline: true,
        lastSeen: lastSeen || new Date().toISOString(),
      });
    });

    // Rider offline
    socket.on("rider:offline", (payload = {}) => {
      const { riderId, lastSeen, rider } = payload;
      if (!riderId) return;

      upsertRider({
        ...(rider || {}),
        _id: riderId,
        isOnline: false,
        lastSeen: lastSeen || new Date().toISOString(),
      });
    });
    // Heartbeat
    socket.on("rider:heartbeat", (payload = {}) => {
      const { riderId, lastSeen, rider } = payload;
      if (!riderId) return;

      upsertRider({
        ...(rider || {}),
        _id: riderId,
        isOnline: true,
        lastSeen: lastSeen || new Date().toISOString(),
      });
    });

    // Global rider location
    socket.on("rider:location", (payload = {}) => {
      const { riderId, lat, lng } = payload;
      if (!riderId || typeof lat !== "number" || typeof lng !== "number")
        return;

      setLiveLocations((prev) => ({
        ...prev,
        [riderId]: { lat, lng, ts: Date.now() },
      }));

      upsertRider({
        _id: riderId,
        isOnline: true,
      });
    });

    // Order-specific location
    socket.on("order:rider-location", (payload = {}) => {
      const { orderId, lat, lng } = payload;
      if (!orderId || typeof lat !== "number" || typeof lng !== "number")
        return;

      setOrderLocations((prev) => ({
        ...prev,
        [orderId]: { lat, lng, ts: Date.now() },
      }));
    });

    socket.on("disconnect", () => {
      console.log("❌ RiderTracking socket disconnected");
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  /* ------------------------------------------------------
     JOIN ORDER ROOM WHEN SELECTED
  ------------------------------------------------------ */
  useEffect(() => {
    if (!socketRef.current || !selectedOrderId) return;
    socketRef.current.emit("order:join", selectedOrderId);
  }, [selectedOrderId]);

  /* ------------------------------------------------------
     FILTERED RIDERS
  ------------------------------------------------------ */
  const filteredRiders = useMemo(() => {
    return riders.filter((r) => {
      const matchSearch = `${r.name ?? ""} ${r.phone ?? ""}`
        .toLowerCase()
        .includes(search.toLowerCase());

      if (!matchSearch) return false;

      if (statusFilter === "online") return !!r.isOnline;
      if (statusFilter === "offline") return !r.isOnline;

      return true;
    });
  }, [riders, search, statusFilter]);

  const selectedRider = useMemo(
    () => riders.find((r) => r._id === selectedRiderId) || null,
    [riders, selectedRiderId]
  );

  const selectedRiderOrders = useMemo(
    () => orders.filter((o) => o.rider && o.rider._id === selectedRiderId),
    [orders, selectedRiderId]
  );

  const selectedOrder = useMemo(
    () => orders.find((o) => o._id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  /* ------------------------------------------------------
     LOCATION HELPERS
  ------------------------------------------------------ */
  const formatTimeAgo = (ts) => {
    if (!ts) return "N/A";
    const diff = Date.now() - ts;
    const seconds = Math.round(diff / 1000);

    if (seconds < 10) return "Just now";
    if (seconds < 60) return `${seconds}s ago`;
    const mins = Math.round(seconds / 60);
    if (mins < 60) return `${mins} min${mins > 1 ? "s" : ""} ago`;
    const hrs = Math.round(mins / 60);
    return `${hrs} hr${hrs > 1 ? "s" : ""} ago`;
  };

  const getRiderLocation = (riderId) => {
    const live = liveLocations[riderId];
    if (live) return live;

    const rider = riders.find((r) => r._id === riderId);
    if (
      rider?.currentLocation &&
      typeof rider.currentLocation.lat === "number" &&
      typeof rider.currentLocation.lng === "number"
    ) {
      return {
        lat: rider.currentLocation.lat,
        lng: rider.currentLocation.lng,
        ts: rider.lastSeen ? new Date(rider.lastSeen).getTime() : null,
      };
    }

    return null;
  };

  const getOrderLocation = (orderId) => orderLocations[orderId] || null;

  const openInMaps = (lat, lng) => {
    if (typeof lat !== "number" || typeof lng !== "number") return;
    const url = `https://www.google.com/maps?q=${lat},${lng}`;
    window.open(url, "_blank", "noopener");
  };

  const buildMapEmbedUrl = (lat, lng) =>
    `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`;

  const onlineCount = riders.filter((r) => r.isOnline).length;

  // Map focus: priority order:
  // 1) Selected order live location
  // 2) Selected rider live/last location
  const focusedLocation = (() => {
    if (selectedOrder) {
      const oloc = getOrderLocation(selectedOrder._id);
      if (oloc) return oloc;
    }
    if (selectedRider) {
      const rloc = getRiderLocation(selectedRider._id);
      if (rloc) return rloc;
    }
    return null;
  })();

  /* ------------------------------------------------------
     RENDER
  ------------------------------------------------------ */
  return (
    <Box sx={{ p: 2 }}>
      {/* HEADER CARD */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          borderRadius: 2,
          border: "1px solid #e3e6e8",
          bgcolor: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Rider Live Tracking
          </Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.5 }}>
            Monitor online riders, their current locations, and assigned orders
            in real time.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mt: 1.5, flexWrap: "wrap" }}>
            <Chip
              size="small"
              icon={<DirectionsBikeIcon sx={{ fontSize: 16 }} />}
              label={`Total riders: ${riders.length}`}
              sx={{ bgcolor: "#f3f4f6", fontWeight: 600 }}
            />
            <Chip
              size="small"
              label={`Online: ${onlineCount}`}
              sx={{
                bgcolor: "#e6f4ea",
                color: "#137333",
                fontWeight: 600,
              }}
            />
            <Chip
              size="small"
              label={`Orders: ${orders.length}`}
              sx={{
                bgcolor: "#e8f0fe",
                color: "#1a3e9a",
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          {/* Status Filter */}
          <ToggleButtonGroup
            size="small"
            exclusive
            value={statusFilter}
            onChange={(_, val) => val && setStatusFilter(val)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="online">Online</ToggleButton>
            <ToggleButton value="offline">Offline</ToggleButton>
          </ToggleButtonGroup>

          {/* Search */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: "1px solid #ddd",
              borderRadius: 2,
              px: 1,
              width: 230,
              bgcolor: "#fafafa",
            }}
          >
            <SearchIcon sx={{ color: "#888", fontSize: 20 }} />
            <input
              placeholder="Search riders..."
              style={{
                border: "none",
                outline: "none",
                padding: "6px 8px",
                width: "100%",
                fontSize: 13,
                background: "transparent",
              }}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>

          <IconButton onClick={load}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* MAIN LAYOUT: RIDER LIST + DETAILS */}
      <Grid container spacing={2}>
        {/* LEFT: RIDERS LIST */}
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 2,
              border: "1px solid #e3e6e8",
              bgcolor: "#fff",
              display: "flex",
              flexDirection: "column",
              height: "calc(100vh - 180px)",
              minHeight: 320,
            }}
          >
            <Box
              sx={{
                p: 2,
                borderBottom: "1px solid #eef0f3",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                Riders ({filteredRiders.length})
              </Typography>

              <Chip
                icon={<DirectionsBikeIcon sx={{ fontSize: 16 }} />}
                label={`${onlineCount} online`}
                size="small"
                sx={{
                  bgcolor: "#e6f4ea",
                  color: "#137333",
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto" }}>
              {filteredRiders.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    No riders match your filters.
                  </Typography>
                </Box>
              )}

              <List dense>
                {filteredRiders.map((r) => {
                  const loc = getRiderLocation(r._id);
                  const isSelected = selectedRiderId === r._id;
                  const assignedCount = orders.filter(
                    (o) => o.rider && o.rider._id === r._id
                  ).length;

                  return (
                    <ListItemButton
                      key={r._id}
                      selected={isSelected}
                      onClick={() => {
                        setSelectedRiderId(r._id);
                        setSelectedOrderId(null);
                      }}
                      sx={{
                        borderBottom: "1px solid #f3f4f6",
                        alignItems: "flex-start",
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                              {r.name}
                            </Typography>
                            <Chip
                              label={r.isOnline ? "Online" : "Offline"}
                              size="small"
                              sx={{
                                bgcolor: r.isOnline ? "#d7ffd9" : "#ffeaea",
                                color: r.isOnline ? "#0f7b32" : "#b40000",
                                fontWeight: 700,
                                fontSize: 10,
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              sx={{ fontSize: 12, color: "text.secondary" }}
                            >
                              {r.phone || "No phone"}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                mt: 0.5,
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={`${assignedCount} orders`}
                                size="small"
                                sx={{
                                  bgcolor: "#eef2f6",
                                  fontSize: 11,
                                }}
                              />
                              {loc && (
                                <Chip
                                  icon={
                                    <AccessTimeIcon sx={{ fontSize: 14 }} />
                                  }
                                  label={formatTimeAgo(loc.ts)}
                                  size="small"
                                  sx={{
                                    bgcolor: "#fff7e6",
                                    fontSize: 11,
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />

                      <ListItemSecondaryAction>
                        {loc && (
                          <IconButton
                            size="small"
                            onClick={() => openInMaps(loc.lat, loc.lng)}
                          >
                            <MyLocationIcon fontSize="small" />
                          </IconButton>
                        )}
                      </ListItemSecondaryAction>
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* RIGHT: MAP + ORDERS */}
        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {/* LIVE LOCATION + MAP */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e3e6e8",
                  bgcolor: "#fff",
                  p: 2.5,
                  minHeight: 260,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    alignItems: "center",
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Live Location
                  </Typography>

                  {selectedRider && (
                    <Chip
                      icon={<RoomIcon sx={{ fontSize: 16 }} />}
                      label={selectedRider.name}
                      sx={{ bgcolor: "#eef2ff", fontWeight: 600 }}
                    />
                  )}
                </Box>

                {!selectedRider && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    Select a rider from the left to see their live position on
                    the map.
                  </Typography>
                )}

                {selectedRider && !focusedLocation && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    No live location received yet for this rider.
                  </Typography>
                )}

                {selectedRider && focusedLocation && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 2,
                        alignItems: "center",
                        mb: 1.5,
                        flexWrap: "wrap",
                      }}
                    >
                      <Chip
                        icon={<RadioButtonCheckedIcon sx={{ fontSize: 14 }} />}
                        label="Live"
                        sx={{
                          bgcolor: "#e6f4ea",
                          color: "#137333",
                          fontWeight: 700,
                        }}
                      />

                      <Typography sx={{ fontSize: 13 }}>
                        Last update: {formatTimeAgo(focusedLocation.ts)}
                      </Typography>

                      <Typography
                        sx={{ fontSize: 13, color: "text.secondary" }}
                      >
                        {selectedOrder
                          ? `Tracking order #${selectedOrder._id}`
                          : "Tracking rider global location"}
                      </Typography>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 4,
                        flexWrap: "wrap",
                        mb: 1.5,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Latitude
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {focusedLocation.lat.toFixed(6)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Longitude
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {focusedLocation.lng.toFixed(6)}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: 12,
                            textTransform: "uppercase",
                            color: "text.secondary",
                          }}
                        >
                          Status
                        </Typography>
                        <Typography sx={{ fontWeight: 600 }}>
                          {selectedRider.isOnline
                            ? "Active & Online"
                            : "Offline (last known position)"}
                        </Typography>
                      </Box>
                    </Box>

                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<MyLocationIcon />}
                      onClick={() =>
                        openInMaps(focusedLocation.lat, focusedLocation.lng)
                      }
                      sx={{ mb: 2 }}
                    >
                      Open in Google Maps
                    </Button>

                    {/* Embedded Google Map */}
                    <Box
                      sx={{
                        borderRadius: 2,
                        overflow: "hidden",
                        border: "1px solid #d0d7de",
                        height: 280,
                      }}
                    >
                      <iframe
                        title="Rider live location"
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        src={buildMapEmbedUrl(
                          focusedLocation.lat,
                          focusedLocation.lng
                        )}
                      />
                    </Box>
                  </>
                )}
              </Paper>
            </Grid>

            {/* RIDER ORDERS */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: "1px solid #e3e6e8",
                  bgcolor: "#fff",
                  p: 2.5,
                  minHeight: 200,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 2,
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 0.3 }}
                    >
                      Rider Orders
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                      Select an order to subscribe to its live location updates.
                    </Typography>
                  </Box>

                  {selectedOrder && (
                    <Chip
                      icon={<TimelineIcon sx={{ fontSize: 16 }} />}
                      label={`Watching order #${selectedOrder._id}`}
                      sx={{ bgcolor: "#e8f2ff", fontWeight: 600 }}
                    />
                  )}
                </Box>

                {!selectedRider && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    Select a rider on the left to see their assigned orders.
                  </Typography>
                )}

                {selectedRider && selectedRiderOrders.length === 0 && (
                  <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                    This rider has no assigned orders.
                  </Typography>
                )}

                {selectedRider && selectedRiderOrders.length > 0 && (
                  <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                    {selectedRiderOrders.map((o) => {
                      const loc = getOrderLocation(o._id);
                      const isActive = selectedOrderId === o._id;

                      return (
                        <Paper
                          key={o._id}
                          sx={{
                            p: 1.5,
                            mb: 1,
                            borderRadius: 2,
                            border: isActive
                              ? "1px solid #0f7bcb"
                              : "1px solid #eef0f3",
                            bgcolor: isActive ? "#f0f7ff" : "#f9fafb",
                            cursor: "pointer",
                          }}
                          onClick={() => setSelectedOrderId(o._id)}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                              Order #{o._id}
                            </Typography>

                            <Chip
                              label={o.status}
                              size="small"
                              sx={{
                                textTransform: "capitalize",
                                bgcolor:
                                  o.status === "completed"
                                    ? "#d1fad2"
                                    : o.status === "shipped"
                                    ? "#cce9ff"
                                    : "#ffe7d7",
                                color:
                                  o.status === "completed"
                                    ? "#0f7b32"
                                    : o.status === "shipped"
                                    ? "#0f4aac"
                                    : "#b86d00",
                                fontWeight: 700,
                                fontSize: 11,
                              }}
                            />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 13,
                              mt: 0.5,
                              color: "text.secondary",
                            }}
                          >
                            {o.user?.name} • {o.user?.phone}
                          </Typography>

                          {loc && (
                            <Typography
                              sx={{
                                fontSize: 12,
                                mt: 0.5,
                                color: "text.secondary",
                              }}
                            >
                              Last location: {loc.lat.toFixed(5)},{" "}
                              {loc.lng.toFixed(5)} ({formatTimeAgo(loc.ts)})
                            </Typography>
                          )}
                        </Paper>
                      );
                    })}
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}
